import { Card, CardContent } from '@/components/ui/card';
import { Users, Trash2, Wrench, BadgeCheck, ArrowDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatRate } from '@/utils/dateFieldStandards';

interface QualifiedFunnelProps {
  /** All leads created in the range */
  cohortLeads: number;
  /** Not a real opportunity (wrong number, spam, out of scope…) */
  excluded: number;
  /** Lost because the business could not serve it (no tech available…) */
  lostOps: number;
  /** cohortLeads − excluded − lostOps */
  gradable: number;
  booked: number;
  /** Booked jobs completed or tech-cancelled on site */
  heldNumerator: number;
  /** Booked jobs completed or cancelled */
  heldDenominator: number;
  /** Click handler for the EXCLUDED pill — opens the QA sheet */
  onExcludedClick?: () => void;
}

type Tone = 'blue' | 'red' | 'amber' | 'green';

interface PillProps {
  icon: typeof Users;
  label: string;
  value: number;
  tone: Tone;
  onClick?: () => void;
  hint?: string;
}

function Pill({ icon: Icon, label, value, tone, onClick, hint }: PillProps) {
  const toneClasses: Record<Tone, string> = {
    blue: 'border-info/40 text-info',
    red: 'border-destructive/40 text-destructive',
    amber: 'border-warning/40 text-warning',
    green: 'border-success/40 text-success',
  };

  const iconBg: Record<Tone, string> = {
    blue: 'bg-info/10 text-info',
    red: 'bg-destructive/10 text-destructive',
    amber: 'bg-warning/10 text-warning',
    green: 'bg-success/10 text-success',
  };

  const interactive = !!onClick;
  const Comp = interactive ? 'button' : 'div';

  return (
    <Comp
      onClick={onClick}
      title={hint}
      className={cn(
        'flex flex-col items-center gap-2',
        interactive && 'cursor-pointer hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary rounded-lg',
      )}
    >
      <div className={cn('h-12 w-12 rounded-full flex items-center justify-center', iconBg[tone])}>
        <Icon className="h-6 w-6" />
      </div>
      <div
        className={cn(
          'px-4 py-1 rounded-full border-2 text-sm font-semibold whitespace-nowrap',
          toneClasses[tone],
          interactive && 'underline-offset-2 hover:underline',
        )}
      >
        {label}: {value}
      </div>
    </Comp>
  );
}

interface FunnelRowProps {
  label: string;
  value: number;
  subtitle: string;
  /** Width of the bar as percentage of container (visual funneling) */
  widthPct: number;
  tone: 'blue' | 'green' | 'amber';
  /** Optional rate annotation shown on the right with arrow; null rate renders "–" */
  annotation?: { rate: number | null; label: string };
}

function FunnelRow({ label, value, subtitle, widthPct, tone, annotation }: FunnelRowProps) {
  const bgClass = {
    blue: 'bg-info',
    green: 'bg-success',
    amber: 'bg-warning',
  }[tone];

  // Clamp so very small bars are still visible/legible
  const safeWidth = Math.max(Math.min(widthPct, 100), value > 0 ? 18 : 0);

  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 flex justify-center">
        <div
          className={cn('rounded-md text-white text-center py-3 px-4 transition-all', bgClass)}
          style={{ width: `${safeWidth}%` }}
        >
          <div className="text-xs font-bold tracking-wider opacity-90">{label}</div>
          <div className="text-base font-semibold mt-0.5">{subtitle}</div>
        </div>
      </div>
      {annotation ? (
        <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground w-24 shrink-0">
          <ArrowDown className="h-3 w-3" />
          <span>
            {annotation.label}:{' '}
            <span className="font-semibold text-foreground">{formatRate(annotation.rate)}</span>
          </span>
        </div>
      ) : (
        <div className="w-24 shrink-0" />
      )}
    </div>
  );
}

export function QualifiedFunnel({
  cohortLeads,
  excluded,
  lostOps,
  gradable,
  booked,
  heldNumerator,
  heldDenominator,
  onExcludedClick,
}: QualifiedFunnelProps) {
  const bookRate = gradable > 0 ? (booked / gradable) * 100 : 0;
  const heldRate = heldDenominator > 0 ? (heldNumerator / heldDenominator) * 100 : null;
  // Held bar is anchored against gradable to keep widths comparable
  const heldWidth = gradable > 0 ? (heldNumerator / gradable) * 100 : 0;

  return (
    <Card>
      <CardContent className="pt-6 space-y-6">
        {/* Equation strip: TOTAL − EXCLUDED − LOST TO OPS = GRADABLE */}
        <div className="flex items-end justify-around gap-2">
          <Pill icon={Users} label="TOTAL" value={cohortLeads} tone="blue" />
          <span className="text-3xl font-light text-muted-foreground pb-3 select-none">−</span>
          <Pill
            icon={Trash2}
            label="EXCLUDED"
            value={excluded}
            tone="red"
            onClick={excluded > 0 ? onExcludedClick : undefined}
            hint={excluded > 0 ? 'Not a real opportunity — click to review' : 'Not a real opportunity'}
          />
          <span className="text-3xl font-light text-muted-foreground pb-3 select-none">−</span>
          <Pill
            icon={Wrench}
            label="LOST TO OPS"
            value={lostOps}
            tone="amber"
            hint="No tech available / tech did not call. Operational losses are not graded on this page."
          />
          <span className="text-3xl font-light text-muted-foreground pb-3 select-none">=</span>
          <Pill icon={BadgeCheck} label="GRADABLE" value={gradable} tone="green" />
        </div>

        {/* Funnel bars */}
        <div className="space-y-2 pt-2">
          <FunnelRow
            label="GRADABLE LEADS"
            value={gradable}
            subtitle={`${gradable} Leads (100%)`}
            widthPct={100}
            tone="blue"
          />
          <FunnelRow
            label="BOOKED"
            value={booked}
            subtitle={`${booked} Booked`}
            widthPct={bookRate}
            tone="green"
            annotation={{ rate: bookRate, label: 'Booking' }}
          />
          <FunnelRow
            label="HELD"
            value={heldNumerator}
            subtitle={`${heldNumerator} of ${heldDenominator} decided`}
            widthPct={heldWidth}
            tone="amber"
            annotation={{ rate: heldRate, label: 'Held' }}
          />
        </div>
      </CardContent>
    </Card>
  );
}
